import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBuilding,
    FaCheckCircle,
    FaCoins,
    FaCubes,
    FaFingerprint,
    FaGlobe,
    FaKey,
    FaLayerGroup,
    FaLock,
    FaMicrochip,
    FaShieldAlt,
    FaUniversity
} from "react-icons/fa";
import {
    TiArrowForward,
    TiArrowRight,
    TiArrowUp
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import WhitepaperModal from './WhitepaperModal';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';
import { whitepaperContents } from './whitepaperData';

gsap.registerPlugin(ScrollTrigger);

const Compliance = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedWhitepaper, setSelectedWhitepaper] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const whitepapers = [
    {
      id: "WP-01",
      title: "Zero-Bank Whitepaper",
      subtitle: "Institutional Banking & Liquidity",
      desc: "Architectural blueprint for the Zero-Bank ecosystem, detailing sovereign liquidity pools and offshore synergy for institutional capital management.",
      icon: <FaUniversity />,
      highlights: ["Liquidity Provisioning", "Strategic Reserves", "Institutional Yield"],
      content: whitepaperContents["WP-01"],
      fileName: "1-Zero-Bank Whitepaper.docx"
    },
    {
      id: "WP-02",
      title: "Zero-Blockchain Whitepaper",
      subtitle: "L1 Architecture & Specs",
      desc: "Technical specification of the Zero-Block L1, focusing on the Proof-of-Sovereign consensus mechanism and high-throughput validation protocols.",
      icon: <FaCubes />,
      highlights: ["Proof-of-Sovereign", "Grid Topology", "Latency Optimization"],
      content: whitepaperContents["WP-02"],
      fileName: "2-Zero-Blockchain Whitepaper.docx"
    },
    {
      id: "WP-03",
      title: "Zero-Coin Whitepaper",
      subtitle: "Ecosystem Utility Ledger",
      desc: "Defining the utility, distribution models, and governance logic of the $ZERO token within the global 12-brand alliance.",
      icon: <FaCoins />,
      highlights: ["Tokenomics", "Governance Utility", "Ecosystem Staking"],
      content: whitepaperContents["WP-03"],
      fileName: "3-Zero-Coin Whitepaper.docx"
    },
    {
      id: "WP-04",
      title: "Zero-Tokens Whitepaper",
      subtitle: "Asset Fractionalization",
      desc: "The protocol for tokenizing real-world assets (RWA), institutional debt, and high-value digital collectibles using Zero-Blockchain architecture.",
      icon: <FaLayerGroup />,
      highlights: ["RWA Tokenization", "Liquidity Bridges", "Asset Security"],
      content: whitepaperContents["WP-04"],
      fileName: "4-Zero-Tokens Whitepaper.docx"
    },
    {
      id: "WP-05",
      title: "Consensus Report ZC-1",
      subtitle: "Validation & Trust Metrics",
      desc: "The final technical audit and performance report on the ZC-1 consensus mechanism, detailing Byzantine Fault Tolerance and uptime assurance.",
      icon: <FaKey />,
      highlights: ["Audit Clearance", "BFT Validation", "Uptime Assurance"],
      content: whitepaperContents["WP-05"],
      fileName: "Zero-Blockchain_Consensus_Report_ZC-1_Final.docx"
    }
  ];

  const deploymentProducts = [
    {
      title: "Zero Blockchain",
      id: "1",
      role: "Core Ledger",
      desc: "Foundational infrastructure for all sovereign data validation and smart contract deployment.",
      icon: <FaMicrochip />,
      color: "blue"
    },
    {
      title: "Zero Comply",
      id: "7",
      role: "Regulatory Engine",
      desc: "Automated KYC/AML synchronization engine utilizing the high-speed ReguSync protocol.",
      icon: <FaFingerprint />,
      color: "cyan"
    },
    {
      title: "Zero Supervise",
      id: "8",
      role: "Oversight Node",
      desc: "Real-time network monitoring and automated reporting portal for institutional transparency.",
      icon: <FaShieldAlt />,
      color: "indigo"
    },
    {
      title: "Zero Insure",
      id: "11",
      role: "Risk Management",
      desc: "Sovereign decentralized insurance protocols protecting high-value ecosystem liquidity assets.",
      icon: <FaLock />,
      color: "emerald"
    },
    {
      title: "Zero Bank",
      id: "21",
      role: "Capital Nexus",
      desc: "Gateway for institutional capital and strategic multi-currency asset management.",
      icon: <FaBuilding />,
      color: "sky"
    }
  ];

  const frameworkSteps = [
    { title: "Jurisdiction Directives", desc: "Setting global regulatory parameters and specific jurisdiction compliance logic." },
    { title: "Institutional Verification", desc: "Deep-level KYC and institutional-grade AML validation for high-volume entities." },
    { title: "ReguSync Integration", desc: "Synchronizing local node protocols with the global real-time ReguSync framework." },
    { title: "Permanent Certification", desc: "Activation of certified sovereign validation nodes for permanent ledger integrity." }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/compliance-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="complianceHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Integrity Protocol"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Grade Regulatory Verification Hub
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Whitepaper Vault (Bento) */}
        <div className="mb-48">
           <div className="mb-16 flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-xl">
                 <div className="text-[10px] font-bold text-blue-500 uppercase tracking-[0.4em] mb-4">Strategic Technical Documentation</div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight">Whitepaper Vault</h2>
              </div>
              <p className="text-gray-300 italic text-sm max-w-xs md:text-right">
                 Official technical specifications and economic directives for the Zero-Ecosystem.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6 auto-rows-[300px]">
              {whitepapers.map((wp, index) => (
                <motion.div
                  key={wp.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative group rounded-[3rem] bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all hover:bg-white/10 overflow-hidden flex flex-col p-10 cursor-pointer ${
                    index === 0 ? "md:col-span-6 lg:col-span-8" : 
                    index === 1 ? "md:col-span-6 lg:col-span-4" :
                    index === 2 ? "md:col-span-6 lg:col-span-4" :
                    index === 3 ? "md:col-span-6 lg:col-span-4" :
                    "md:col-span-6 lg:col-span-4"
                  }`}
                  ref={(el) => (sectionRefs.current[index] = el)}
                  onClick={() => {
                    setSelectedWhitepaper(wp);
                    setIsModalOpen(true);
                  }}
                >
                  <div className="absolute top-8 right-10 text-4xl text-blue-500/20 group-hover:scale-110 transition-transform">
                     {wp.icon}
                  </div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{wp.id}</div>
                  <h3 className="text-2xl font-bold text-white mb-2 tracking-widest">{wp.title}</h3>
                  <div className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mb-6">{wp.subtitle}</div>
                  <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{wp.desc}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto">
                     {wp.highlights.map((h, i) => (
                       <span key={i} className="text-[8px] font-bold text-gray-600 border border-white/5 px-3 py-1 rounded-full uppercase tracking-widest group-hover:border-blue-500/20 group-hover:text-gray-300 transition-colors">
                          {h}
                       </span>
                     ))}
                  </div>

                  <motion.div 
                    whileHover={{ x: 5 }}
                    className="absolute bottom-10 right-10 text-blue-500 cursor-pointer z-20"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent double trigger if bubbling somehow happens
                      setSelectedWhitepaper(wp);
                      setIsModalOpen(true);
                    }}
                  >
                     <TiArrowForward className="text-3xl" />
                  </motion.div>
                </motion.div>
              ))}
           </div>
        </div>

        {/* Institutional Nexus Grid */}
        <div className="mb-48">
           <div className="mb-16 text-center max-w-2xl mx-auto">
              <div className="text-[10px] font-bold text-cyan-500 uppercase tracking-[0.4em] mb-4">Deployment Infrastructure</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight mb-8">Institutional Nexus</h2>
              <p className="text-gray-300 text-sm leading-relaxed italic">
                 Explore the deep-tech platforms engineering the 12-brand alliance for global synergy.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {deploymentProducts.map((prod, index) => (
                <motion.a
                  key={prod.id}
                  href={`/products/${prod.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-8 rounded-[2.5rem] bg-white/5 border border-white/10 hover:border-blue-500/40 hover:bg-blue-900/10 transition-all flex flex-col items-center text-center group h-full"
                >
                  <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform shadow-[0_0_30px_rgba(59,130,246,0.1)]">
                     {prod.icon}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 tracking-widest uppercase">{prod.title}</h3>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.3em] mb-4">{prod.role}</div>
                  <p className="text-[11px] text-gray-500 leading-relaxed italic pr-2">{prod.desc}</p>
                </motion.a>
              ))}
           </div>
        </div>

        {/* Compliance Lifecycle Timeline */}
        <div className="flex flex-col lg:flex-row gap-24 items-center mb-48">
           <div className="w-full lg:w-1/2">
              <div className="text-[10px] font-bold text-indigo-500 uppercase tracking-[0.4em] mb-4">Framework Lifecycle</div>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight mb-12">Regulatory Deployment <span className="text-indigo-500">Protocol</span></h2>
              
              <div className="space-y-12 relative">
                 <div className="absolute left-6 top-8 bottom-8 w-[1px] bg-white/10" />
                 {frameworkSteps.map((step, idx) => (
                   <motion.div 
                     key={idx}
                     onViewportEnter={() => setActiveStep(idx)}
                     className={`relative pl-16 group cursor-default transition-opacity duration-700 ${activeStep === idx ? 'opacity-100' : 'opacity-30'}`}
                   >
                      <div className={`absolute left-0 top-0 w-12 h-12 rounded-full border flex items-center justify-center transition-all ${activeStep === idx ? 'bg-indigo-600 border-indigo-400 shadow-[0_0_20px_rgba(79,70,229,0.5)]' : 'bg-black border-white/20'}`}>
                         <div className={`w-2 h-2 rounded-full ${activeStep === idx ? 'bg-white' : 'bg-white/20'}`} />
                      </div>
                      <h4 className="text-xl font-bold text-white mb-2 tracking-widest uppercase">{step.title}</h4>
                      <p className="text-gray-500 text-sm italic">{step.desc}</p>
                   </motion.div>
                 ))}
              </div>
           </div>

           <div className="w-full lg:w-1/2 p-2 rounded-[4rem] bg-gradient-to-br from-indigo-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl relative group">
              <div className="bg-black/80 backdrop-blur-3xl p-16 relative">
                 <div className="flex items-center gap-4 mb-12">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-2xl">
                       <FaCheckCircle />
                    </div>
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.4em]">Integrated Status: SYNCED</div>
                 </div>
                 <div className="grid grid-cols-2 gap-8">
                    {[
                      { l: "Primary Jurisdiction", v: "Global Digital" },
                      { l: "Verification Tier", v: "Institutional Tier-1" },
                      { l: "Audit Node", v: "ZC-Validator-Primary" },
                      { l: "ReguSync Engine", v: "Active V4.2" }
                    ].map((item, i) => (
                      <div key={i} className="flex flex-col gap-2">
                         <div className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">{item.l}</div>
                         <div className="text-sm font-bold text-white italic tracking-widest">{item.v}</div>
                      </div>
                    ))}
                 </div>
                 <div className="mt-16 pt-8 border-t border-white/5 flex items-center justify-center">
                    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "98%" }}
                        transition={{ duration: 2.5, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                       />
                    </div>
                 </div>
                 <div className="mt-4 flex justify-between text-[8px] font-bold text-gray-600 uppercase tracking-widest">
                    <span>Protocol Synchronizing</span>
                    <span>98%</span>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-indigo-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[3rem] bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-5xl text-indigo-400 mx-auto mb-16 shadow-[0_0_50px_rgba(79,70,229,0.2)]">
                <FaShieldAlt />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Initialize <span className="font-zentry italic text-indigo-400 text-8xl uppercase tracking-tighter block mt-4">Institutional</span> Sync
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Secure the latest technical whitepapers and institutional archives for the Zero-Ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="blue"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   Download Archive (.ZIP)
                 </GlassButton>
                 
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4">
                     Access Compliance Hub <TiArrowRight className="text-2xl text-blue-400" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe /> REGULATORY SYNERGY</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaBuilding /> INSTITUTIONAL TRUST</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaFingerprint /> SECURE VALIDATION</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-indigo-500/30 rounded-full shadow-lg shadow-indigo-500/10 cursor-pointer hover:bg-black/60 hover:border-indigo-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-indigo-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />

      {/* Whitepaper Modal */}
      <WhitepaperModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        whitepaper={selectedWhitepaper}
      />
    </div>
  );
};

export default Compliance;
